import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Deposit } from '../../types';

interface FailDepositModalProps {
  deposit: Deposit | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}

const FailDepositModal: React.FC<FailDepositModalProps> = ({
  deposit,
  open,
  onClose,
  onConfirm,
  loading,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open, deposit?._id]);

  if (!deposit) return null;

  const handleConfirm = () => {
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject deposit</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will mark the deposit as failed. The user will not be credited.
        </Alert>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Amount
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {deposit.amount} ETB
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Phone
          </Typography>
          <Typography variant="body1">{deposit.phoneNumber}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bank
          </Typography>
          <Typography variant="body1">{deposit.bankType}</Typography>
          {deposit.reference && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Reference
              </Typography>
              <Typography variant="body1">{deposit.reference}</Typography>
            </>
          )}
        </Box>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          placeholder="e.g. Wrong account, duplicate, invalid SMS…"
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Rejecting…' : 'Reject deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FailDepositModal;
